<script setup>
// custom: T8a — barra de busca local estilo WhatsApp para a lista de conversas
//
// Filtro client-side sobre conversationList já carregada — sem chamada à rede.
// v-model para searchQuery em ChatList.vue. Debounce 200ms (mais responsivo que
// os 500ms da busca global, já que é operação local).
// Atalho "/" foca; Esc limpa. RTL espelha via classes ltr:/rtl:.
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { debounce } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const inputRef = ref(null);
const localValue = ref(props.modelValue);

// custom: T8a fix — sincroniza localValue quando o pai zera modelValue
// (v-model é one-way: o pai pode zerar searchQuery ao trocar contexto)
watch(
  () => props.modelValue,
  val => {
    if (val !== localValue.value) {
      localValue.value = val;
    }
  }
);

const emitDebounced = debounce(value => {
  emit('update:modelValue', value);
}, 200);

function onInput(e) {
  localValue.value = e.target.value;
  emitDebounced(localValue.value);
}

function clear() {
  localValue.value = '';
  emit('update:modelValue', '');
  inputRef.value?.focus();
}

function onKeydown(e) {
  if (e.key === '/') {
    // Foca a busca apenas quando o foco não está em outro input/textarea
    const tag = document.activeElement?.tagName?.toLowerCase();
    if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') {
      e.preventDefault();
      inputRef.value?.focus();
    }
  }
  if (e.key === 'Escape') {
    clear();
    inputRef.value?.blur();
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div class="px-3 pt-1 pb-2">
    <!-- custom: T8a fix — pílula de busca estilo WhatsApp: cápsula suave preenchida
         (sem borda dura sempre visível, que ficava "solta" no light mode). Borda
         só no foco. bg-n-alpha-2 (light) / dark:bg-n-solid-1 segue o idioma do
         ContactHeader do próprio Chatwoot. -->
    <div
      class="flex items-center gap-2 ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2 h-9 rounded-full bg-n-alpha-2 dark:bg-n-solid-1 border border-transparent focus-within:border-n-slate-12 focus-within:bg-n-background dark:focus-within:bg-n-solid-2 transition-colors duration-mj-toque"
    >
      <span
        class="i-lucide-search size-4 text-n-slate-10 flex-shrink-0 ltr:order-first rtl:order-last"
      />
      <!-- custom: T8a fix2 — `reset-base` tira o input do $form-input-selector global
           (_base.scss): sem ele, o input herdava `field-base` (outline + rounded-lg +
           bg + h-10 + mb-4) e virava uma 2ª pílula sobreposta, expulsando a lupa.
           Estilos próprios abaixo (text-mj-15/text-n-slate-12/bg-transparent/outline-none)
           cobrem tudo que o field-base dava. -->
      <input
        ref="inputRef"
        :value="localValue"
        type="text"
        :placeholder="t('CHAT_LIST.SEARCH.PLACEHOLDER')"
        class="reset-base flex-1 bg-transparent text-mj-15 leading-none text-n-slate-12 placeholder:text-n-slate-10 outline-none min-w-0"
        @input="onInput"
      />
      <button
        v-if="localValue"
        type="button"
        class="grid place-items-center flex-shrink-0 size-5 rounded-full text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors duration-mj-toque ltr:order-last rtl:order-first"
        :title="t('CHAT_LIST.SEARCH.CLEAR')"
        @click="clear"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>
  </div>
</template>
